import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  LogOut, Search, FileText, Video, Music, Image, 
  Download, Tag, Bookmark, BookmarkCheck, X, Filter,
  File, ArrowLeft, FolderOpen, Eye, ExternalLink, CheckCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import LoadingQuip from "@/components/LoadingQuip";
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

// IndexedDB Cache for Practice Resources
const DB_NAME = 'PracticeResourcesCache';
const DB_VERSION = 1;
const STORE_NAME = 'files';

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'path' });
      }
    };
  });
};

// Get cached file from IndexedDB
const getCachedFile = async (path) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(path);
      
      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.blob);
        } else {
          resolve(null);
        }
      };
    });
  } catch (error) {
    console.error('Cache read error:', error);
    return null;
  }
};

// Store file in IndexedDB cache
const cacheFile = async (path, blob) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ path, blob, cachedAt: Date.now() });
      
      request.onerror = () => resolve(false);
      request.onsuccess = () => resolve(true);
    });
  } catch (error) {
    console.error('Cache write error:', error);
    return false;
  }
};

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getFileIcon(fileType) {
  switch (fileType) {
    case 'video': return <Video className="w-5 h-5" />;
    case 'audio': return <Music className="w-5 h-5" />;
    case 'image': return <Image className="w-5 h-5" />;
    case 'pdf': return <FileText className="w-5 h-5" />;
    default: return <File className="w-5 h-5" />;
  }
}

// Audio Player Component - handles authenticated audio streaming with caching
function AudioPlayer({ subfolder, filename, API_URL }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    let blobUrl = null;
    const cachePath = `audio/${subfolder}/${filename}`;
    
    const loadAudio = async () => {
      try {
        // Check cache first
        const cachedBlob = await getCachedFile(cachePath);
        if (cachedBlob && isMounted) {
          blobUrl = URL.createObjectURL(cachedBlob);
          setAudioUrl(blobUrl);
          setFromCache(true);
          setLoading(false);
          return;
        }
        
        const token = localStorage.getItem("session_token");
        const response = await fetch(
          `${API_URL}/practice-resources/files/${subfolder}/${filename}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        // Get content length for progress
        const contentLength = response.headers.get('content-length');
        const total = parseInt(contentLength, 10);
        
        // Read the response as a stream for progress tracking
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;
        
        while(true) {
          const {done, value} = await reader.read();
          
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          
          if (total && isMounted) {
            setLoadingProgress(Math.round((receivedLength / total) * 100));
          }
        }
        
        // Combine chunks into a single blob
        const blob = new Blob(chunks, { type: 'audio/mpeg' });
        
        // Cache the file for future use
        await cacheFile(cachePath, blob);
        
        if (isMounted) {
          blobUrl = URL.createObjectURL(blob);
          setAudioUrl(blobUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Audio load error:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };
    
    loadAudio();
    
    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [subfolder, filename, API_URL]);
  
  // Handle download
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(
        `${API_URL}/practice-resources/files/${subfolder}/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-100 rounded-lg">
        <LoadingQuip size="sm" />
        <p className="text-sm text-gray-500 mt-3">Loading audio... {loadingProgress > 0 ? `${loadingProgress}%` : ''}</p>
        <p className="text-xs text-gray-400 mt-1">This may take a moment for large files</p>
        {loadingProgress > 0 && (
          <div className="w-full max-w-xs mt-3 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }
  
  if (error || !audioUrl) {
    return (
      <div className="p-6 bg-red-50 rounded-lg text-center">
        <Music className="w-12 h-12 mx-auto text-red-400 mb-2" />
        <p className="text-red-600 font-medium">Failed to load audio player</p>
        <p className="text-red-500 text-sm mt-1 mb-4">The audio file may be too large to stream in browser</p>
        <button
          onClick={handleDownload}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Download Audio File
        </button>
      </div>
    );
  }
  
  return (
    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
      <div className="flex items-center gap-3 mb-3">
        <Music className="w-6 h-6 text-blue-600" />
        <span className="text-sm font-medium text-gray-700">{filename}</span>
        {fromCache && (
          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" /> Cached
          </span>
        )}
      </div>
      <audio src={audioUrl} controls className="w-full" style={{ height: '40px' }} />
      <div className="mt-2 text-right">
        <button
          onClick={handleDownload}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Download file
        </button>
      </div>
    </div>
  );
}

// Video Player Component - handles authenticated video streaming with caching
function VideoPlayer({ subfolder, filename, API_URL }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  useEffect(() => {
    let isMounted = true;
    const cachePath = `video/${subfolder}/${filename}`;
    
    const loadVideo = async () => {
      try {
        // Check cache first
        const cachedBlob = await getCachedFile(cachePath);
        if (cachedBlob && isMounted) {
          const url = URL.createObjectURL(cachedBlob);
          setVideoUrl(url);
          setFromCache(true);
          setLoading(false);
          return;
        }
        
        const token = localStorage.getItem("session_token");
        const response = await fetch(
          `${API_URL}/practice-resources/files/${subfolder}/${filename}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        // Get content length for progress
        const contentLength = response.headers.get('content-length');
        const total = parseInt(contentLength, 10);
        
        // Read the response as a stream for progress tracking
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;
        
        while(true) {
          const {done, value} = await reader.read();
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          
          if (total && isMounted) {
            setLoadingProgress(Math.round((receivedLength / total) * 100));
          }
        }
        
        const blob = new Blob(chunks);
        
        // Cache the file for future use
        await cacheFile(cachePath, blob);
        
        if (isMounted) {
          const url = URL.createObjectURL(blob);
          setVideoUrl(url);
          setLoading(false);
        }
      } catch (err) {
        console.error('Video load error:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };
    
    loadVideo();
    
    return () => {
      isMounted = false;
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [subfolder, filename, API_URL]);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-gray-100 rounded-lg">
        <LoadingQuip size="sm" />
        <p className="text-sm text-gray-500 mt-3">
          Loading video... {loadingProgress > 0 ? `${loadingProgress}%` : ''}
        </p>
        <p className="text-xs text-gray-400 mt-1">This may take a moment for large files</p>
        {loadingProgress > 0 && (
          <div className="w-full max-w-xs mt-3 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        )}
      </div>
    );
  }
  
  if (error || !videoUrl) {
    return (
      <div className="p-6 bg-red-50 rounded-lg text-center">
        <Video className="w-12 h-12 mx-auto text-red-400 mb-2" />
        <p className="text-red-600 font-medium">Failed to load video</p>
        <p className="text-red-500 text-sm mt-1">Please try downloading the file instead</p>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {fromCache && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full shadow">
          <CheckCircle className="w-3 h-3" /> Cached
        </div>
      )}
      <video
        src={videoUrl}
        controls
        className="w-full"
        style={{ maxHeight: '500px' }}
      />
    </div>
  );
}

// File Preview Component
// AuthenticatedImage component - loads images with authentication and caching
function AuthenticatedImage({ filePath, filename, API_URL }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem("session_token");
    const parts = filePath.split('/');
    const subfolder = parts[parts.length - 2];
    const fname = parts[parts.length - 1];
    const cachePath = `image/${subfolder}/${fname}`;
    
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check cache first
        const cachedBlob = await getCachedFile(cachePath);
        if (cachedBlob && isMounted) {
          const blobUrl = URL.createObjectURL(cachedBlob);
          setImageUrl(blobUrl);
          setFromCache(true);
          setLoading(false);
          return;
        }
        
        const response = await fetch(
          `${API_URL}/practice-resources/files/${subfolder}/${fname}`,
          { 
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const blob = await response.blob();
        if (!isMounted) return;
        
        if (blob.size === 0) {
          throw new Error('Empty response');
        }
        
        // Cache the file for future use
        await cacheFile(cachePath, blob);
        
        const blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
        setLoading(false);
      } catch (err) {
        console.error('Error loading image:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load');
          setLoading(false);
        }
      }
    };
    
    loadImage();
    
    return () => {
      isMounted = false;
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [filePath, API_URL, retryCount]);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-100 rounded-lg min-h-[200px]">
        <LoadingQuip size="sm" />
        <p className="text-xs text-gray-500 mt-2">Loading image...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-4 bg-gray-100 rounded-lg text-center min-h-[200px]">
        <p className="text-gray-600 mb-2">Image loading issue</p>
        <button 
          onClick={() => setRetryCount(c => c + 1)}
          className="text-blue-600 hover:text-blue-800 underline text-sm"
        >
          Try again
        </button>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {fromCache && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full shadow">
          <CheckCircle className="w-3 h-3" /> Cached
        </div>
      )}
      <img
        src={imageUrl}
        alt={filename}
        className="max-w-full h-auto mx-auto"
        style={{ maxHeight: '500px' }}
        onError={() => setError('Image display error')}
      />
    </div>
  );
}

function FilePreview({ attachment, getAuthenticatedFileUrl, API_URL }) {
  const isImage = attachment.file_type === 'image';
  const isVideo = attachment.file_type === 'video';
  const isAudio = attachment.file_type === 'audio';
  const isPdf = attachment.file_type === 'pdf';
  
  // For large files, use alternative loading approach
  const isLargeFile = attachment.file_size > 50 * 1024 * 1024; // > 50MB
  const isLargeImage = isImage && attachment.file_size > 2 * 1024 * 1024; // > 2MB for images
  
  // Skip useEffect for images, audio, and large videos - they use dedicated components
  const skipBlobLoading = isImage || isAudio || (isVideo && isLargeFile) || isLargeImage;
  
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(!skipBlobLoading);
  const [error, setError] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  
  useEffect(() => {
    // Skip blob loading for images, audio, and large videos - use dedicated components
    if (skipBlobLoading) {
      return;
    }
    
    let isMounted = true;
    
    const loadFile = async () => {
      try {
        const url = await getAuthenticatedFileUrl(attachment.file_path);
        if (isMounted) {
          setFileUrl(url);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Failed to load");
          setLoading(false);
        }
      }
    };
    
    loadFile();
    
    return () => { isMounted = false; };
  }, [attachment.file_path, getAuthenticatedFileUrl, skipBlobLoading]);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-100 rounded-lg">
        <LoadingQuip size="sm" />
        {isLargeFile && (
          <p className="text-xs text-gray-500 mt-2">Loading large file...</p>
        )}
      </div>
    );
  }
  
  // For audio files, use AudioPlayer component
  if (isAudio) {
    const parts = attachment.file_path.split('/');
    const subfolder = parts[parts.length - 2];
    const filename = parts[parts.length - 1];
    
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <AudioPlayer 
          subfolder={subfolder} 
          filename={filename} 
          API_URL={API_URL}
        />
      </div>
    );
  }
  
  // For large videos, use direct URL approach with auth header via fetch
  if (isVideo && (isLargeFile || !fileUrl)) {
    const parts = attachment.file_path.split('/');
    const subfolder = parts[parts.length - 2];
    const filename = parts[parts.length - 1];
    
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <VideoPlayer 
          subfolder={subfolder} 
          filename={filename} 
          API_URL={API_URL}
        />
      </div>
    );
  }
  
  // For images, use dedicated AuthenticatedImage component
  if (isImage) {
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <AuthenticatedImage 
          filePath={attachment.file_path}
          filename={attachment.filename}
          API_URL={API_URL}
        />
      </div>
    );
  }
  
  if (!fileUrl || error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-center text-red-600">
        Failed to load file
      </div>
    );
  }
  
  return (
    <div className="rounded-lg overflow-hidden border border-gray-200">
      
      {isVideo && (
        <video
          src={fileUrl}
          controls
          className="w-full"
          style={{ maxHeight: '500px' }}
        />
      )}
      
      {isPdf && (
        <>
          <div 
            className="relative bg-white rounded border-2 border-gray-200 overflow-hidden"
            style={{ height: '400px' }}
          >
            <embed
              src={`${fileUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              className="w-full h-full"
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              onClick={() => setShowPdfModal(true)}
              variant="outline"
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Full PDF
            </Button>
            <Button
              onClick={() => window.open(fileUrl, '_blank')}
              variant="outline"
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
          </div>
          
          <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
            <DialogContent className="max-w-5xl h-[90vh] overflow-hidden p-0">
              <DialogHeader className="px-6 pt-6 pb-2">
                <DialogTitle>{attachment.filename}</DialogTitle>
              </DialogHeader>
              <div className="w-full px-6 pb-6" style={{ height: 'calc(90vh - 80px)' }}>
                <object
                  data={fileUrl}
                  type="application/pdf"
                  className="w-full h-full rounded border border-gray-300"
                >
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500">
                    <p className="font-medium mb-4">Unable to display PDF in browser.</p>
                    <p className="text-sm">Please download the file to view it.</p>
                  </div>
                </object>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default function PracticeResources({ user, logout }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  
  // Theme-aware colors
  const colors = {
    heading: isDarkMode ? '#ffffff' : '#092036',
    subtext: isDarkMode ? '#e2e8f0' : '#70767C',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    mainBg: isDarkMode ? '#092036' : '#F4F4F4',
    headerBg: isDarkMode ? '#092036' : 'white',
    border: isDarkMode ? '#1e3a5f' : '#DADEEO',
    badgeText: isDarkMode ? '#93c5fd' : '#102862',
    badgeBorder: isDarkMode ? '#60a5fa' : '#102862',
    badgeSecondaryBg: isDarkMode ? '#1e3a5f' : '#f1f5f9',
    badgeSecondaryText: isDarkMode ? '#e2e8f0' : '#64748b',
  };
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  
  // Detail dialog
  const [selectedResource, setSelectedResource] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchResources = useCallback(async () => {
    try {
      let url = `${API}/practice-resources`;
      const params = new URLSearchParams();
      
      if (selectedCategory) params.append("category_id", selectedCategory);
      if (selectedTag) params.append("tag", selectedTag);
      if (searchQuery) params.append("search", searchQuery);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers });
      setResources(response.data);
    } catch (error) {
      toast.error("Error loading resources");
    }
  }, [selectedCategory, selectedTag, searchQuery]);

  const fetchBookmarks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/practice-resources/user/bookmarks`, { headers });
      setResources(response.data);
    } catch (error) {
      toast.error("Error loading bookmarks");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resource-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories");
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/practice-resources/tags/all`, { headers });
      setAllTags(response.data);
    } catch (error) {
      console.error("Error loading tags");
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchTags()]);
      if (showBookmarksOnly) {
        await fetchBookmarks();
      } else {
        await fetchResources();
      }
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchTags, fetchResources, fetchBookmarks, showBookmarksOnly]);

  // Debounced search
  useEffect(() => {
    if (!showBookmarksOnly) {
      const timer = setTimeout(() => {
        fetchResources();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, selectedCategory, selectedTag, showBookmarksOnly, fetchResources]);

  const toggleBookmark = async (resourceId, e) => {
    e?.stopPropagation();
    try {
      const response = await axios.post(
        `${API}/practice-resources/${resourceId}/bookmark`,
        {},
        { headers }
      );
      
      // Update local state
      setResources(prev => prev.map(r => 
        r.id === resourceId 
          ? { ...r, is_bookmarked: response.data.bookmarked }
          : r
      ));
      
      // Update selected resource if open
      if (selectedResource?.id === resourceId) {
        setSelectedResource(prev => ({ ...prev, is_bookmarked: response.data.bookmarked }));
      }
      
      toast.success(response.data.message);
      
      // If viewing bookmarks only and unbookmarked, remove from list
      if (showBookmarksOnly && !response.data.bookmarked) {
        setResources(prev => prev.filter(r => r.id !== resourceId));
      }
    } catch (error) {
      toast.error("Error updating bookmark");
    }
  };

  const [fileUrls, setFileUrls] = useState({});
  
  const getAuthenticatedFileUrl = useCallback(async (filePath) => {
    // Check in-memory cache first
    if (fileUrls[filePath]) {
      return fileUrls[filePath];
    }
    
    const token = localStorage.getItem("session_token");
    const parts = filePath.split('/');
    const subfolder = parts[parts.length - 2];
    const filename = parts[parts.length - 1];
    const cachePath = `file/${subfolder}/${filename}`;
    
    try {
      // Check IndexedDB cache
      const cachedBlob = await getCachedFile(cachePath);
      if (cachedBlob) {
        const blobUrl = URL.createObjectURL(cachedBlob);
        setFileUrls(prev => ({ ...prev, [filePath]: blobUrl }));
        return blobUrl;
      }
      
      const response = await axios.get(
        `${API}/practice-resources/files/${subfolder}/${filename}`,
        { 
          headers: { Authorization: `Bearer ${token}` }, 
          responseType: 'blob',
          timeout: 300000 // 5 minute timeout for large files
        }
      );
      
      if (!response.data || response.data.size === 0) {
        console.error('Empty response data for file:', filename);
        return null;
      }
      
      // Cache the file in IndexedDB
      await cacheFile(cachePath, response.data);
      
      const blobUrl = URL.createObjectURL(response.data);
      setFileUrls(prev => ({ ...prev, [filePath]: blobUrl }));
      return blobUrl;
    } catch (error) {
      console.error("Error loading file:", error);
      return null;
    }
  }, [fileUrls]);

  const downloadFile = async (filePath, filename) => {
    try {
      const url = await getAuthenticatedFileUrl(filePath);
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Error downloading file");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedTag("");
    setShowBookmarksOnly(false);
  };

  const openResourceDetail = (resource) => {
    setSelectedResource(resource);
    setShowDetailDialog(true);
  };

  const filteredResources = showBookmarksOnly 
    ? resources 
    : resources;

  return (
    <div className="min-h-screen grid-layout-page" style={{ background: colors.mainBg }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white shadow-sm'}`} style={{ backgroundColor: colors.headerBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Logo" className="h-8 sm:h-10 flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <span className="text-sm sm:text-lg font-semibold truncate" style={{ color: colors.heading }}>
                Practice Resources
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <DarkModeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3"
                style={{ color: colors.subtext }}
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="w-8 h-8 sm:w-10 sm:h-10"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#9E3639' }} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 sm:pb-8 sm:pt-8">
        {/* Filters */}
        <Card className="p-4 mb-6" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="pl-9"
              />
            </div>
            
            {/* Category Filter */}
            <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full lg:w-48">
                <FolderOpen className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Tag Filter */}
            <Select value={selectedTag || "all"} onValueChange={(v) => setSelectedTag(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full lg:w-48">
                <Tag className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="All Tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Bookmarks Toggle */}
            <Button
              variant={showBookmarksOnly ? "default" : "outline"}
              onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
              className={`gap-2 ${showBookmarksOnly ? 'text-white' : ''}`}
              style={showBookmarksOnly ? { background: '#102862' } : {}}
            >
              {showBookmarksOnly ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              Bookmarks
            </Button>
            
            {/* Clear Filters */}
            {(searchQuery || selectedCategory || selectedTag || showBookmarksOnly) && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="gap-1"
              >
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Resources Grid */}
        {loading ? (
          <Card className="p-12" style={{ backgroundColor: colors.cardBg }}>
            <LoadingQuip size="lg" />
          </Card>
        ) : filteredResources.length === 0 ? (
          <Card className="p-12 text-center" style={{ backgroundColor: colors.cardBg }}>
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: colors.border }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: colors.subtext }}>
              {showBookmarksOnly ? "No bookmarked resources" : "No resources found"}
            </h3>
            <p style={{ color: colors.subtext }}>
              {showBookmarksOnly 
                ? "Bookmark resources to access them quickly later"
                : "Try adjusting your filters or search query"
              }
            </p>
            {(searchQuery || selectedCategory || selectedTag || showBookmarksOnly) && (
              <Button
                onClick={clearFilters}
                variant="outline"
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredResources.map(resource => (
              <Card 
                key={resource.id} 
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
                onClick={() => openResourceDetail(resource)}
              >
                <div className="p-4">
                  {/* Header with bookmark */}
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg flex-1 pr-2" style={{ color: colors.heading }}>
                      {resource.title}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => toggleBookmark(resource.id, e)}
                      className={resource.is_bookmarked ? "text-yellow-500" : ""}
                      style={{ color: resource.is_bookmarked ? undefined : colors.subtext }}
                    >
                      {resource.is_bookmarked 
                        ? <BookmarkCheck className="w-5 h-5" /> 
                        : <Bookmark className="w-5 h-5" />
                      }
                    </Button>
                  </div>
                  
                  {/* Description */}
                  {resource.description && (
                    <p className="text-sm mb-3 line-clamp-2" style={{ color: colors.subtext }}>
                      {resource.description}
                    </p>
                  )}
                  
                  {/* Category and Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {resource.category_name && (
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ borderColor: colors.badgeBorder, color: colors.badgeText }}
                      >
                        {resource.category_name}
                      </Badge>
                    )}
                    {resource.tags?.slice(0, 3).map(tag => (
                      <Badge 
                        key={tag} 
                        variant="secondary" 
                        className="text-xs"
                        style={{ backgroundColor: colors.badgeSecondaryBg, color: colors.badgeSecondaryText }}
                      >
                        {tag}
                      </Badge>
                    ))}
                    {resource.tags?.length > 3 && (
                      <Badge 
                        variant="secondary" 
                        className="text-xs"
                        style={{ backgroundColor: colors.badgeSecondaryBg, color: colors.badgeSecondaryText }}
                      >
                        +{resource.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Attachments indicator */}
                  {resource.attachments?.length > 0 && (
                    <div className="flex items-center gap-3 text-sm" style={{ color: colors.subtext }}>
                      {resource.attachments.map((att, idx) => (
                        <span key={idx} className="flex items-center gap-1">
                          {getFileIcon(att.file_type)}
                        </span>
                      )).slice(0, 4)}
                      {resource.attachments.length > 4 && (
                        <span>+{resource.attachments.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Resource Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0" style={{ backgroundColor: colors.cardBg }}>
          {selectedResource && (
            <div className="flex flex-col h-full max-h-[90vh] overflow-y-auto">
              {/* Header with Title */}
              <div className="p-6 border-b" style={{ borderColor: colors.border }}>
                <DialogHeader>
                  <DialogTitle className="text-xl pr-10" style={{ color: colors.heading }}>{selectedResource.title}</DialogTitle>
                </DialogHeader>
              </div>
              
              {/* MOBILE LAYOUT - Attachments First */}
              <div className="lg:hidden">
                {/* Attachments Section - Mobile First */}
                {selectedResource.attachments?.length > 0 && (
                  <div className="p-4" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f9fafb' }}>
                    <h4 className="text-sm font-semibold mb-4" style={{ color: colors.heading }}>
                      Attachments ({selectedResource.attachments.length})
                    </h4>
                    <div className="space-y-4">
                      {selectedResource.attachments.map((att, idx) => (
                        <div key={idx} className="border rounded-lg overflow-hidden" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white', borderColor: colors.border }}>
                          {/* File Header */}
                          <div className="flex items-center justify-between p-3 border-b" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white', borderColor: colors.border }}>
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              {getFileIcon(att.file_type)}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate" style={{ color: colors.heading }}>{att.filename}</p>
                                <p className="text-xs" style={{ color: colors.subtext }}>{formatFileSize(att.file_size)}</p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadFile(att.file_path, att.filename)}
                              className="gap-1 flex-shrink-0 ml-2"
                              style={{ borderColor: colors.border, color: colors.subtext }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* File Preview */}
                          <div className="p-3">
                            <FilePreview 
                              attachment={att} 
                              getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                              API_URL={API}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Content Section - Mobile Second */}
                <div className="space-y-4 p-4">
                  {/* Description */}
                  {selectedResource.description && (
                    <p style={{ color: colors.subtext }}>{selectedResource.description}</p>
                  )}
                  
                  {/* Category and Tags */}
                  <div className="flex flex-wrap gap-2">
                    {selectedResource.category_name && (
                      <Badge 
                        variant="outline"
                        style={{ borderColor: colors.badgeBorder, color: colors.badgeText }}
                      >
                        <FolderOpen className="w-3 h-3 mr-1" />
                        {selectedResource.category_name}
                      </Badge>
                    )}
                    {selectedResource.tags?.map(tag => (
                      <Badge 
                        key={tag} 
                        variant="secondary"
                        style={{ backgroundColor: colors.badgeSecondaryBg, color: colors.badgeSecondaryText }}
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Content */}
                  {selectedResource.content && (
                    <div className="rounded-lg p-4" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f9fafb' }}>
                      <h4 className="text-sm font-semibold mb-2" style={{ color: colors.heading }}>
                        Content
                      </h4>
                      <div 
                        className="prose prose-sm max-w-none text-sm" 
                        style={{ color: colors.subtext }}
                        dangerouslySetInnerHTML={{ __html: selectedResource.content }}
                      />
                    </div>
                  )}
                  
                  {/* Metadata */}
                  <div className="text-xs pt-4 border-t" style={{ color: colors.subtext, borderColor: colors.border }}>
                    <p>
                      Added by {selectedResource.created_by_name || 'Admin'} on{' '}
                      {new Date(selectedResource.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* DESKTOP LAYOUT - Side by Side */}
              <div className="hidden lg:flex lg:flex-row flex-1">
                {/* Left Side - Content */}
                <div className="lg:w-1/2 p-6 overflow-y-auto" style={{ borderRight: `1px solid ${colors.border}` }}>
                  <div className="space-y-4">
                    {/* Description */}
                    {selectedResource.description && (
                      <p style={{ color: colors.subtext }}>{selectedResource.description}</p>
                    )}
                    
                    {/* Category and Tags */}
                    <div className="flex flex-wrap gap-2">
                      {selectedResource.category_name && (
                        <Badge 
                          variant="outline"
                          style={{ borderColor: colors.badgeBorder, color: colors.badgeText }}
                        >
                          <FolderOpen className="w-3 h-3 mr-1" />
                          {selectedResource.category_name}
                        </Badge>
                      )}
                      {selectedResource.tags?.map(tag => (
                        <Badge 
                          key={tag} 
                          variant="secondary"
                          style={{ backgroundColor: colors.badgeSecondaryBg, color: colors.badgeSecondaryText }}
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    {/* Content */}
                    {selectedResource.content && (
                      <div className="rounded-lg p-4" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f9fafb' }}>
                        <h4 className="text-sm font-semibold mb-2" style={{ color: colors.heading }}>
                          Content
                        </h4>
                        <div 
                          className="prose prose-sm max-w-none text-sm" 
                          style={{ color: colors.subtext }}
                          dangerouslySetInnerHTML={{ __html: selectedResource.content }}
                        />
                      </div>
                    )}
                    
                    {/* Metadata */}
                    <div className="text-xs pt-4 border-t" style={{ color: colors.subtext, borderColor: colors.border }}>
                      <p>
                        Added by {selectedResource.created_by_name || 'Admin'} on{' '}
                        {new Date(selectedResource.created_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Right Side - Attachments */}
                <div className="lg:w-1/2 p-6 overflow-y-auto" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f9fafb' }}>
                  {selectedResource.attachments?.length > 0 ? (
                    <div>
                      <h4 className="text-sm font-semibold mb-4" style={{ color: colors.heading }}>
                        Attachments ({selectedResource.attachments.length})
                      </h4>
                      <div className="space-y-4">
                        {selectedResource.attachments.map((att, idx) => (
                          <div key={idx} className="border rounded-lg overflow-hidden" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white', borderColor: colors.border }}>
                            {/* File Header */}
                            <div className="flex items-center justify-between p-3 border-b" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white', borderColor: colors.border }}>
                              <div className="flex items-center gap-3">
                                {getFileIcon(att.file_type)}
                                <div>
                                  <p className="text-sm font-medium" style={{ color: colors.heading }}>{att.filename}</p>
                                  <p className="text-xs" style={{ color: colors.subtext }}>{formatFileSize(att.file_size)}</p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadFile(att.file_path, att.filename)}
                                className="gap-1"
                                style={{ borderColor: colors.border, color: colors.subtext }}
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </Button>
                            </div>
                            
                            {/* File Preview */}
                            <div className="p-3">
                              <FilePreview 
                                attachment={att} 
                                getAuthenticatedFileUrl={getAuthenticatedFileUrl}
                                API_URL={API}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full" style={{ color: colors.subtext }}>
                      <p>No attachments</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
